import { Button, useTextFilter } from "@cynthai/ui";
import { useDebounceFn } from "@reactuses/core";
import { useEffect, useState } from "react";
import { ListBox, ListBoxItem } from "react-aria-components";

import { DEFAULT_SUGGESTIONS } from "@/features/help-center/components/lib/constants";
import { useTopics } from "@/features/help-center/hooks/use-topics";
import { m } from "@/paraglide/messages";

type Props = {
  query: string;
  minLength?: number;
  onSelect: (value: string) => void;
  suggestions?: readonly string[];
};

function HighlightedText({
  text,
  query,
}: Readonly<{ text: string; query: string }>) {
  const q = query.trim();
  if (!q) return <>{text}</>;

  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, idx)}
      <span className="bg-primary/20 rounded-lg px-px font-medium">
        {text.slice(idx, idx + q.length)}
      </span>
      {text.slice(idx + q.length)}
    </>
  );
}

export default function AutoSuggest({
  query,
  minLength = 1,
  onSelect,
  suggestions = DEFAULT_SUGGESTIONS,
}: Readonly<Props>) {
  const [items, setItems] = useState<string[]>([]);
  const topics = useTopics().data ?? [];
  const matches = useTextFilter();

  const { run: performSearch } = useDebounceFn((searchQuery: string) => {
    if (!searchQuery || searchQuery.length < minLength) {
      setItems([]);
      return;
    }
    setItems(suggestions.filter((t) => matches(searchQuery, t)));
  }, 300);

  useEffect(() => {
    performSearch(query);
  }, [query, performSearch]);

  const matchingTopics = query.trim()
    ? topics.filter((t) => matches(query, t.name))
    : [];

  if (items.length === 0 && matchingTopics.length === 0) return null;

  return (
    <div className="z-50 mx-auto w-full max-w-2xl px-2">
      <div
        className={`border-tertiary bg-background-2 rounded-md border p-1 shadow-lg`}
      >
        {items.length > 0 && (
          <ListBox
            aria-label={m.search_suggestions()}
            className="divide-tertiary max-h-56 divide-y overflow-auto"
            onAction={(key) => onSelect(key as string)}
          >
            {items.map((s) => (
              <ListBoxItem
                key={s}
                id={s}
                className="data-focused:bg-background-2 cursor-pointer px-4 py-3 outline-none"
              >
                <div className="text-text-primary text-sm">
                  <HighlightedText text={s} query={query} />
                </div>
              </ListBoxItem>
            ))}
          </ListBox>
        )}

        {matchingTopics.length > 0 && (
          <div className="flex items-center gap-2 px-4 pt-2 pb-3">
            <div className="text-text-secondary text-xs">{m.topics()}</div>
            <div className="flex flex-wrap justify-end gap-2">
              {matchingTopics.map((t) => (
                <Button
                  key={t.id}
                  variant="ghost"
                  size="unsized"
                  onPress={() => onSelect(t.name)}
                  className={`bg-primary/20 text-text-primary inline-block cursor-pointer rounded-md px-2 py-1 text-xs transition hover:opacity-90`}
                >
                  {t.name}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
